import { isContentClean, getFilterMessage } from '../../utils/contentFilter';
import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  VStack,
  HStack,
  Box,
  Text,
  Input,
  Textarea,
  Switch,
  useToast,
  Spinner,
} from '@chakra-ui/react';
import { useAuth } from '../../contexts/AuthContext';
import ProfileAvatar from './ProfileAvatar';

interface EditPersonalInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EditPersonalInfoModal: React.FC<EditPersonalInfoModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { userProfile, currentUser, updatePersonalInfo, updateProfilePhoto, refreshProfile } = useAuth();
  const toast = useToast();
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    username: '',
    phoneNumber: '',
    email: '',
    dateOfBirth: '',
    bio: '',
    showFullName: true,
  });

  // Initialize form with current profile data
  useEffect(() => {
    if (userProfile && isOpen) {
      setFormData({
        fullName: userProfile.fullName || '',
        username: userProfile.username || '',
        phoneNumber: userProfile.phoneNumber || '',
        email: userProfile.email || '',
        dateOfBirth: userProfile.dateOfBirth || '',
        bio: userProfile.bio || '',
        showFullName: userProfile.showFullName ?? true,
      });
    }
  }, [userProfile, isOpen]);

  const handleChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpdate = async (newPhotoURL: string) => {
    try {
      await updateProfilePhoto(newPhotoURL);
    } catch (error) {
      console.error('Error updating photo:', error);
    }
  };

  const handleSave = async () => {
    if (!currentUser) return;

    // Validate username
    if (!formData.username.trim()) {
      toast({
        title: 'Username required',
        description: 'Please enter a username',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (formData.username.length < 3) {
      toast({
        title: 'Username too short',
        description: 'Username must be at least 3 characters',
        status: 'error',
        duration: 3000,
      });
      return;
    }
    if (!isContentClean(formData.username)) {
      toast({ title: 'Invalid username', description: getFilterMessage('username'), status: 'error', duration: 4000 });
      return;
    }

    setLoading(true);

    try {
      await updatePersonalInfo({
        fullName: formData.fullName.trim(),
        username: formData.username.trim(),
        phoneNumber: formData.phoneNumber.trim(),
        dateOfBirth: formData.dateOfBirth,
        bio: formData.bio.trim(),
        showFullName: formData.showFullName,
      });

      toast({
        title: 'Profile updated',
        description: 'Your changes have been saved',
        status: 'success',
        duration: 2000,
      });

      await refreshProfile();
      onClose();
    } catch (error: any) {
      console.error('Error saving profile:', error);
      toast({
        title: 'Error',
        description: error.message || 'Could not save changes',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const inputStyles = {
    bg: '#1C1C1E',
    border: 'none',
    borderRadius: 'xl',
    color: 'white',
    py: 6,
    px: 4,
    fontSize: 'md',
    _placeholder: { color: 'gray.500' },
    _focus: { 
      boxShadow: '0 0 0 1px rgba(255, 107, 53, 0.5)',
      bg: '#252528',
    },
    _hover: { bg: '#252528' },
  };

  const labelStyles = {
    color: 'gray.400',
    fontSize: 'xs',
    textTransform: 'lowercase' as const,
    mb: 1,
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full" motionPreset="slideInBottom">
      <ModalOverlay bg="blackAlpha.900" />
      <ModalContent bg="black" m={0} borderRadius={0}>
        {/* Header */}
        <HStack 
          justify="space-between" 
          px={4} 
          py={4}
          borderBottom="1px solid"
          borderColor="gray.800"
        >
          <Box
            as="button"
            color="gray.400"
            fontSize="md"
            onClick={onClose}
            _hover={{ color: 'white' }}
          >
            Cancel
          </Box>
          <Text color="white" fontSize="md" fontWeight="semibold">
            Edit Profile
          </Text>
          <Box
            as="button"
            color="orange.400"
            fontSize="md"
            fontWeight="semibold"
            cursor={loading ? 'not-allowed' : 'pointer'}
            onClick={loading ? undefined : handleSave}
            opacity={loading ? 0.5 : 1}
            _hover={{ color: 'orange.300' }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            minW="50px"
          >
            {loading ? <Spinner size="sm" color="orange.400" /> : 'Save'}
          </Box>
        </HStack>

        <ModalBody py={6} px={4} overflowY="auto">
          <VStack spacing={6} align="stretch" maxW="500px" mx="auto">
            {/* Profile Picture */}
            <Box display="flex" justifyContent="center">
              <ProfileAvatar
                userId={currentUser?.uid || ''}
                photoURL={userProfile?.photoURL}
                fullName={formData.fullName}
                username={formData.username}
                size="xl"
                editable
                onPhotoUpdate={handlePhotoUpdate}
              />
            </Box>

            {/* Full Name */}
            <Box>
              <Text {...labelStyles}>Full name</Text>
              <Input
                {...inputStyles}
                value={formData.fullName}
                onChange={(e) => handleChange('fullName', e.target.value)}
                placeholder="Enter your full name"
              />
            </Box>

            {/* Show Name Visibility Toggle */}
            <HStack 
              bg="#1C1C1E" 
              p={4} 
              borderRadius="xl"
              justify="space-between"
            >
              <Box flex={1}>
                <HStack spacing={3}>
                  <Switch
                    isChecked={formData.showFullName}
                    onChange={(e) => handleChange('showFullName', e.target.checked)}
                    colorScheme="orange"
                    size="lg"
                  />
                  <Text color="gray.400" fontSize="sm">
                    Show the visibility of first and last names.
                  </Text>
                </HStack>
              </Box>
            </HStack>

            {/* Username */}
            <Box>
              <Text {...labelStyles}>Username</Text>
              <Input
                {...inputStyles}
                value={formData.username}
                onChange={(e) => handleChange('username', e.target.value.toLowerCase().replace(/\s/g, ''))}
                placeholder="username"
              />
            </Box>

            {/* Phone Number */}
            <Box>
              <Text {...labelStyles}>phone number</Text>
              <Input
                {...inputStyles}
                value={formData.phoneNumber}
                onChange={(e) => handleChange('phoneNumber', e.target.value)}
                placeholder="+1 (555) 000-0000"
                type="tel"
              />
            </Box>

            {/* Email (read-only) */}
            <Box>
              <Text {...labelStyles}>Email</Text>
              <HStack>
                <Input
                  {...inputStyles}
                  value={formData.email}
                  isReadOnly
                  flex={1}
                  opacity={0.7}
                />
                <Text 
                  color={userProfile?.kycStatus === 'verified' ? 'green.400' : 'orange.400'} 
                  fontSize="sm" 
                  fontWeight="semibold" 
                  px={2}
                  whiteSpace="nowrap"
                >
                  {userProfile?.kycStatus === 'verified' ? 'Verified' : 'Pending'}
                </Text>
              </HStack>
            </Box>

            {/* Date of Birth */}
            <Box>
              <Text {...labelStyles}>Date of Birth</Text>
              <Input
                {...inputStyles}
                type="date"
                value={formData.dateOfBirth}
                onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                sx={{
                  colorScheme: 'dark',
                  '&::-webkit-calendar-picker-indicator': {
                    filter: 'invert(1)',
                    cursor: 'pointer',
                  },
                }}
              />
            </Box>

            {/* Bio */}
            <Box>
              <Text {...labelStyles}>Bio</Text>
              <Textarea
                {...inputStyles}
                value={formData.bio}
                onChange={(e) => handleChange('bio', e.target.value)}
                placeholder="Tell others about yourself..."
                rows={4}
                resize="none"
                maxLength={200}
              />
              <Text color="gray.600" fontSize="xs" textAlign="right" mt={1}>
                {formData.bio.length}/200
              </Text>
            </Box>

            {/* Spacer for safe area */}
            <Box h="80px" />
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default EditPersonalInfoModal;